/* eslint-disable custom/relay-unused-fields-allow-connection */
// The feature flags that are defined in this file are used in other components/files so the
// unused-fields linting rule doesn't work for this query.
import { createContext, createElement, Suspense } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";

import { useLoadQuery } from "@skydio/relay-toolkit";
import { Skeleton } from "@skydio/rivet-ui";

import type { PropsWithChildren } from "react";
import type { PreloadedQuery } from "react-relay";
import type {
  FeatureFlagProviderQuery,
  FeatureFlagProviderQuery$data,
} from "__generated__/FeatureFlagProviderQuery.graphql";

const query = graphql`
  query FeatureFlagProviderQuery($orgUuid: String!) @team(name: "cloud-infrastructure") {
    organization(uuid: $orgUuid) {
      id
      featureFlags {
        # REQUIRED: Global "dfr_command" feature flag - DO NOT REMOVE
        dfrCommand
        # OPTIONAL: Additional feature flags used in the application
        # These can be modified or extended as needed for specific functionality
        brandingDfrCommand
        enableMultidroneOperations
        enableRfdDedrone
        enableDfrCommandPhaseTwoUi
        enableDfrCommandPhaseTwoPointFiveUi
        enableDfrCommandPhaseThreeUi
        enableDfrDockviewLayout
        customMapLayersExternal
        enableCreateMarkerInteractionRfd
        enableManualClipCapture
        enableDocklessSites
        multizoneSiteGeofences
        safeLandingPoints
        enableDispatchRespondHere
        # NOTE(bella) Markers CRUD API - only used to gate a mutation change before Cloud Release 3
        markersApi
        enableIncidentsStateUi
        enableAxonWatchMeDemo
        enablePostFlightReports
        cloudRel5UiUpdates
        enableDfrOutcomesSurvey
        enablePlannedOperations
        setupScheduledFlightsFromCloud
        enablePlanningAroundGeofencesFlyToPointRfd
        usePathfinderFlyToPointRfd
        apiObservability

        enableRfdViewLookatPoint
        enableMulticockpitRfd
        enableMultidroneRfd
        inmAscendFy26
        missionsInRfdV2
        assetSettingsView
        assetSurveyFromPhotos
        axonAlprIntegration
        enableVideoFeedbackSurvey
        writeAddressForBodycams
        aglFromVehicleRfd
        enableAglWaypointAltitudesRfd
        clearMarkersDispatchMap
        rfdOnSubdomain
        rfdOnSubdomainForEngineering
        markersBackendFiltering
        enableSentryAutodetection
        liveStreaming
        atlasPointsRfdV2
        enableRfdv2SidebarLayout
        playwrightBlankMapRendering
        enableRfdv2SidebarStack
        nomadTrueOrthographicMode
        teleopCommandeeringV2
        atlasPointsRfdV2MarkersOnly
        deploymentEngineerMissionsInternal
        shotspotterIntegration
        consumablesReport
        consumablesUsageAlerts
        consumablesV2Panel
        enableDeclarativeDockview
        enableCustomRfdUrl
        enableRticMode
        enableHistoricalMarkerFilters
        enableMarkerFilterSettings
        splitUserMediaUploadProgress
        frontendBeta
        enableOperationsV0
        displayPathfinderBoundariesInMaps
        pathfinderBoundariesMenu
        rfdCollapsibleSidebar
        rfdPinnableSidebar
        editOrgFlightPolicyR10
        enableDockedRthDeconfliction
        rfdEnableRthOptimalSlider
        enableFlightPathVisualization
        healthStatusChangedAlert
        rfd3dCockpit
        cloudRtkProfiles
        enableTogglingGeofencesRfd
        reliableCommandeering
        completeStacknavSidebar
        enableMultidroneAdsbRfd
        waypointDragReorder
        enableNorthIndicator
        tagSettingsView
        enableAglGeofences
        enableCloudTakMarkers
        enableOutOfService
        fleetMapViewEnhancements
      }
    }
  }
`;

export type FeatureFlags = NonNullable<
  NonNullable<FeatureFlagProviderQuery$data["organization"]>["featureFlags"]
>;

export const featureFlagContext = createContext<FeatureFlags | null>(null);

// The useFeatureFlag hook is now provided in src/hooks/useFeatureFlag.ts
// No need to import it here to avoid circular dependencies

interface InternalProps extends PropsWithChildren {
  queryReference: PreloadedQuery<FeatureFlagProviderQuery>;
}

const FeatureFlagProviderInternal = ({ queryReference, children }: InternalProps) => {
  const queryData = usePreloadedQuery(query, queryReference);

  return (
    <featureFlagContext.Provider value={queryData.organization?.featureFlags ?? null}>
      {children}
    </featureFlagContext.Provider>
  );
};

const FeatureFlagProviderSkeleton = () => {
  // Add your own skeleton loading state here
  return <Skeleton className="h-12" />;
};

const FeatureFlagProvider = ({ orgUuid, ...props }: PropsWithChildren<{ orgUuid: string }>) => {
  const [queryReference] = useLoadQuery<FeatureFlagProviderQuery>(query, {
    orgUuid,
  });

  if (!queryReference) {
    return <FeatureFlagProviderSkeleton />;
  }

  return (
    <Suspense fallback={createElement(FeatureFlagProviderSkeleton)}>
      <FeatureFlagProviderInternal queryReference={queryReference} {...props} />
    </Suspense>
  );
};

export default FeatureFlagProvider;
